import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { socketInstance } from 'lib/socket'
import { queryKeys } from 'constants/queryKeys'
import { useAuthStore } from 'features/auth/store/authStore'
import CustomToast from 'ui/CustomToast'
import { subscribeSocketEvents } from 'hooks/useDebouncedSocketListener'

// Vehicle/auction lifecycle → refresh vehicle lists + auction-by-status queries.
const VEHICLE_EVENTS = [
  'vehicle_expired',
  'vehicle_offer_accept',
  'vehicle_offer_declined',
  'vehicle_back_to_parkingloat',
  'vehicle_launch',
  'vehicle_launch_to_buy',
  'upcoming_vehicle_live',
  'OfferReceive',
  'vehicle_move_to_parking',
  'vehicle_counter_offer_declined',
  'buy_it_now_price_updated',
  'counter_offer_added',
  'vehicle_deleted',
]

// Sale completion → also refresh invoices (purchased/awarded/bill-of-sale).
const SALE_EVENTS = ['vehicle_sold', 'Bought_by_buyer']

const SocketQueryInvalidator = () => {
  const queryClient = useQueryClient()
  const roleSwitchTimeoutRef = useRef(null)

  useEffect(() => {
    const invalidateVehicles = () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.auctions.all })
    }

    const invalidateOnSale = () => {
      invalidateVehicles()
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
    }

    const handleFeatureFlagsUpdated = (payload) => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
      if (payload?.features) {
        useAuthStore.getState().setFeatures(payload.features)
      } else {
        useAuthStore.getState().fetchProfile()
      }
    }

    const handleRoleSwitched = (data) => {
      if (useAuthStore.getState().isSwitchingRole) {
        return
      }

      const currentRole = useAuthStore.getState().role
      const targetRole = data?.role

      if (
        targetRole &&
        currentRole &&
        targetRole.toLowerCase() !== currentRole.toLowerCase()
      ) {
        if (data.message) {
          CustomToast({
            message: data.message,
            type: 'info',
            autoClose: 10000,
          })
        }
        if (roleSwitchTimeoutRef.current) {
          clearTimeout(roleSwitchTimeoutRef.current)
        }
        roleSwitchTimeoutRef.current = setTimeout(() => {
          useAuthStore.getState().setRole(targetRole)
          queryClient.invalidateQueries()
          if (window.location.pathname !== '/') {
            window.location.href = '/'
          }
        }, 10000)
      }
    }

    // vehicle_launch bursts (bulk launch) are debounced (400ms, max 5s); all other
    // events invalidate immediately.
    const unsubscribeVehicles = subscribeSocketEvents(
      VEHICLE_EVENTS,
      invalidateVehicles,
      { delay: 400 },
    )
    const unsubscribeSale = subscribeSocketEvents(
      SALE_EVENTS,
      invalidateOnSale,
      { delay: 400 },
    )
    socketInstance.on('FeatureFlagsUpdated', handleFeatureFlagsUpdated)
    socketInstance.on('ROLE_SWITCHED', handleRoleSwitched)

    return () => {
      if (roleSwitchTimeoutRef.current) {
        clearTimeout(roleSwitchTimeoutRef.current)
      }
      unsubscribeVehicles()
      unsubscribeSale()
      socketInstance.off('FeatureFlagsUpdated', handleFeatureFlagsUpdated)
      socketInstance.off('ROLE_SWITCHED', handleRoleSwitched)
    }
  }, [queryClient])

  return null
}

export default SocketQueryInvalidator
